import {
  CaseReducer,
  CaseReducerWithPrepare,
  createAction,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { setAuthInHeader } from 'api/config/axiosUtils';
import storage, { deleteCookie, getCookie, keys, setCookie, setSessionCookie } from 'lib/storage';
import { all, takeLatest } from 'redux-saga/effects';
import store, { RootState } from 'store';
import { createFetchAction, createSaga, fetchInitialState, fetchReducerActions } from 'store/utils';
import * as authApi from 'api/auth';
import { any } from 'prop-types';
import {
  AppActions,
  AuthActions,
  UserActions,
  PartnerActions,
  PmsActions,
} from 'store/actionCreators';
import { IEnrolledGroupCode } from 'lib/types/authTypes';
// auth
export const sign_in_auth = createFetchAction('sign_in_auth');
export const sign_up_auth = createFetchAction('sign_up_auth');
export const edit_legal_agreement = createFetchAction('edit_legal_agreement');
export const edit_auth_profile = createFetchAction('edit_auth_profile');
export const edit_auth_password = createFetchAction('edit_auth_password');
export const check_group_code = createFetchAction('check_group_code');
export const connect_auth_profile = createFetchAction('connect_auth_profile');
export const connect_auth_password = createFetchAction('connect_auth_password');

// export const accessToken = storage.get(keys.user)?.autoLogin
//   ? getCookie(keys.remember_user_token)
//   : getCookie(keys.sign_in_token) || null;
const { ipcRenderer } = window;

interface InitialState {
  accessToken: string | any;
  recentLogin: string[] | any;
  signUp: object | any;
  signIn: object | any;
  signOut: object | any;
  signInAuth: object | any;
  signUpAuth: object | any;
  editLegalAgreement: object | any;
  editAuthProfile: object | any;
  editAuthPassword: object | any;
  checkGroupCode: object | any;
  enrolledGroupCodes: IEnrolledGroupCode[] | any;
  connectAuthProfile: object | any;
  connectAuthPassword: object | any;
  loginHistory: object | any;
  loginAdminHistory: string | any;
}

const initialState: InitialState = {
  // NOTE: 최초 랜딩시 storage값 유무 확인
  accessToken: storage.get(keys.user)?.autoLogin
    ? getCookie(keys.remember_user_token)
    : getCookie(keys.sign_in_token) || null,
  // accessToken: '',
  recentLogin: getCookie(keys.recent_login)?.split(',') || [],
  // privateSocket: null,
  enrolledGroupCodes: (!ipcRenderer && storage.get(keys.enrolled_group_codes)) || [],
  signUp: { ...fetchInitialState },
  signIn: { ...fetchInitialState },
  signOut: { ...fetchInitialState },
  signInAuth: { ...fetchInitialState },
  signUpAuth: { ...fetchInitialState },
  editLegalAgreement: { ...fetchInitialState },
  editAuthProfile: { ...fetchInitialState },
  editAuthPassword: { ...fetchInitialState },
  checkGroupCode: { ...fetchInitialState },
  connectAuthProfile: { ...fetchInitialState },
  connectAuthPassword: { ...fetchInitialState },
  loginHistory: getCookie(keys.login_history)?.split(',') || [],
  loginAdminHistory: getCookie(keys.login_admin_history),
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    set_access_token: (state, { payload }) => {
      state.accessToken = payload;
      // setAuthInHeader(payload);
    },
    set_recent_login: (state, { payload }) => {
      state.recentLogin = payload;
    },
    set_login_history: (state, { payload }) => {
      state.loginHistory = payload;
    },
    set_login_admin_history: (state, { payload }) => {
      state.loginAdminHistory = payload;
    },
    set_enrolled_group_codes: (
      state,
      { payload }: PayloadAction<IEnrolledGroupCode | IEnrolledGroupCode[]>,
    ) => {
      // Electron에서 받은 groupcode 데이터
      // console.log('set_enrolled_group_codes ____ ', payload);
      if (Array.isArray(payload)) {
        state.enrolledGroupCodes = payload;
      } else {
        const { groupCode, nickname, type, profileImg, email } = payload;
        const codeItemToApply = {
          groupCode,
          nickname,
          type,
          profileImg,
          email,
        };
        // groupCode 같은 항목
        const existingGroupCodes = state.enrolledGroupCodes.filter(
          (codeItem: IEnrolledGroupCode) => codeItem?.groupCode === groupCode,
        );
        const hasExistingGroupCode = existingGroupCodes.length;
        // groupCode 같은 항목이 있지만, type은 같은 게 없는 경우
        const hasEqualTypeGroupCoded = existingGroupCodes.find(
          (codeItem: IEnrolledGroupCode) => codeItem?.type === type,
        );

        let enrolledGroupCodesValue = state.enrolledGroupCodes;
        // get_profile을 통해 왔을 경우, 그룹장일 경우의 이미지 업데이트
        if (hasExistingGroupCode && type === 'manager') {
          enrolledGroupCodesValue = state.enrolledGroupCodes.map((codeItem: IEnrolledGroupCode) => {
            return codeItem?.groupCode === groupCode ? { ...codeItem, profileImg } : codeItem;
          });
        }
        // 없거나, 그룹장 또는 그릅원 한가지만 있을 경우 추가
        if (!hasExistingGroupCode || (hasExistingGroupCode && !hasEqualTypeGroupCoded)) {
          enrolledGroupCodesValue = state.enrolledGroupCodes.concat(codeItemToApply);
          // 추가될 경우 정렬하기 그룹장, 그룹원
          enrolledGroupCodesValue.sort((a: any, b: any) => {
            const aNickName = a.nickname.toLowerCase();
            const bNickName = b.nickname.toLowerCase();

            if (aNickName === bNickName) {
              // employee, manager 내림차순(manager가 앞으로)
              return a.type > b.type ? -1 : 1;
            }
            return aNickName > bNickName ? 1 : -1;
          });
        }
        storage.set(keys.enrolled_group_codes, enrolledGroupCodesValue);
        state.enrolledGroupCodes = enrolledGroupCodesValue;

        if (!!ipcRenderer) {
          // TODO: 테스트 후 연동 필요
          // console.log('ipcRenderer groupCodeAdd send');
          // console.log('codeItemToApply ____ ', codeItemToApply);
          ipcRenderer.send('groupCodeAdd', codeItemToApply);
        }
      }
    },
    delete_enrolled_group_codes: (
      state,
      { payload }: PayloadAction<{ groupCode: string; type: 'manager' | 'employee' }>,
    ) => {
      // console.log('groupCodeDelete payload', payload);
      const { groupCode, type } = payload;
      const enrolledGroupCodesValue = state.enrolledGroupCodes.filter(
        (codeItem: { groupCode: string; type: 'manager' | 'employee' }) =>
          !(codeItem.groupCode === groupCode && codeItem.type === type),
      );

      storage.set(keys.enrolled_group_codes, enrolledGroupCodesValue);
      state.enrolledGroupCodes = enrolledGroupCodesValue;

      if (ipcRenderer) {
        // TODO: 테스트 후 연동 필요
        // console.log('ipcRenderer groupCodeDelete send');
        ipcRenderer.send('groupCodeDelete', payload);
      }
    },
    change_enrolled_group_codes_img: (
      state,
      { payload: projectList }: PayloadAction<IEnrolledGroupCode[]>,
    ) => {
      state.enrolledGroupCodes = state.enrolledGroupCodes.map((codeItem: IEnrolledGroupCode) => {
        const extingCode = projectList.find(
          (project: IEnrolledGroupCode) => project.groupCode === codeItem.groupCode,
        );
        return extingCode ? { ...codeItem, profileImg: extingCode.profileImg } : codeItem;
      });
    },
    sign_up: () => {},
    sign_in: (state, { payload }) => {
      // DEBUG: 백엔드 연결 후 테스트 필요
      // console.log(payload, 'payload signIn');
      // const { token, user } = payload;
      // if (!token) return;
      // state.accessToken = token;
      // setAuthInHeader(token);
      // storage.set(keys.token, token);
      // storage.set(keys.user, user);
    },
    sign_out: (state, { payload }) => {
      // DEBUG: 백엔드 연결 후 테스트 필요
      // state.accessToken = null;
      // setAuthInHeader(null);
      // // storage.clear();
      // storage.remove(keys.token);
      // storage.remove(keys.user);
      // storage.remove(`persist:${keys.persist}`);
      // sessionStorage.removeItem(`persist:${keys.persist}`);
    },
    //
    ...fetchReducerActions(sign_in_auth, 'signInAuth'),
    ...fetchReducerActions(sign_up_auth, 'signUpAuth'),
    ...fetchReducerActions(edit_legal_agreement, 'editLegalAgreement'),
    ...fetchReducerActions(edit_auth_profile, 'editAuthProfile'),
    ...fetchReducerActions(edit_auth_password, 'editAuthPassword'),
    ...fetchReducerActions(check_group_code, 'checkGroupCode'),
    ...fetchReducerActions(connect_auth_profile, 'connectAuthProfile'),
    ...fetchReducerActions(connect_auth_password, 'connectAuthPassword'),
  },
});

export const name = slice.name;
export const actions: any = slice.actions;

// createSelector
export const accessTokenState = (state: RootState) => state.auth.accessToken;
export const isAuthenticatedSelector = createSelector(accessTokenState, item => !!item);
export const logInState = (state: RootState) => ({
  accessToken: state.auth.accessToken,
  user: state.user.user,
});

export const isLogInSelector = createSelector(logInState, item => {
  //TODO: agreementList accept 여부 확인
  return !!item.accessToken && !!item.user;
});

export function connectDofAuth(type: string) {
  const submitData = {
    return_url: window.location.protocol + '//' + window.location.host + '/home',
    failure_url: window.location.protocol + '//' + window.location.host + '/home',
  };
  // console.log('submitData _____________________ ', submitData);
  if (type === 'signIn') {
    AuthActions.sign_in_auth_request(submitData);
  }
  if (type === 'signUp') {
    AuthActions.sign_up_auth_request(submitData);
  }
}

// 정보동의까지 완료, 회원정보 fetch하며 로그인 마무리 단계
export function signInUser(accessToken: string | null, email?: string) {
  // console.log('signInUser on user module');
  // console.log('signInUser ______ ', accessToken);
  if (!accessToken) return;
  setSessionCookie(keys.sign_in_token, accessToken);
  setCookie(keys.remember_user_token, accessToken, { 'max-age': 3600 * 24 * 7 });
  AuthActions.set_access_token(accessToken);
  UserActions.fetch_profile_request();
  // PartnerActions.fetch_partner_request_list_request();

  // set resent login list with email
  if (email) {
    let recentLogin: any = getCookie(keys.recent_login)?.split(',') || [];
    // 증복 제거
    recentLogin = new Set([...recentLogin, email]);
    setCookie(keys.recent_login, Array.from(recentLogin));
    AuthActions.set_recent_login(Array.from(recentLogin));
  }
}

// 로그인 시 로그인 정보 쿠키 저장
export function loginUserHistory(email?: string, nickname?: string) {
  setCookie(keys.login_history, [email, nickname]);
  AuthActions.set_login_history([email, nickname]);
}

export function loginAdminHistory(email?: string) {
  setCookie(keys.login_admin_history, email);
  AuthActions.set_login_admin_history(email);
}

export function signOut() {
  deleteCookie(keys.sign_in_token);
  deleteCookie(keys.remember_user_token);
  storage.remove(keys.user);
  UserActions.set_user(null);
  AuthActions.accessToken = null;
  // persist 삭제
  storage.remove(`persist:${keys.persist}`);
  sessionStorage.removeItem(`persist:${keys.persist}`);
  if (ipcRenderer) {
    ipcRenderer.send('logoutSuccess');
  }
  PmsActions.pms_ready(false);
}

// function* handleSignUp (action) {}

// function* handleSignIn (action) {}

// function* handleSignOut (action) {}

export function* authSaga() {
  yield all([
    takeLatest(
      actions.sign_in_auth_request,
      createSaga(actions, 'sign_in_auth', authApi.signInAuth),
    ),
    takeLatest(
      actions.sign_up_auth_request,
      createSaga(actions, 'sign_up_auth', authApi.signUpAuth),
    ),
    takeLatest(
      actions.edit_legal_agreement_request,
      createSaga(actions, 'edit_legal_agreement', authApi.editLegalAgreement),
    ),
    takeLatest(
      actions.edit_auth_profile_request,
      createSaga(actions, 'edit_auth_profile', authApi.editAuthProfile),
    ),
    takeLatest(
      actions.edit_auth_password_request,
      createSaga(actions, 'edit_auth_password', authApi.editAuthPassword),
    ),
    takeLatest(
      actions.connect_auth_profile_request,
      createSaga(actions, 'connect_auth_profile', authApi.connectAuthProfile),
    ),
    takeLatest(
      actions.connect_auth_password_request,
      createSaga(actions, 'connect_auth_password', authApi.connectAuthProfile),
    ),
    takeLatest(
      actions.check_group_code_request,
      createSaga(actions, 'check_group_code', authApi.checkGroupCode, { isAlertfailure: false }),
    ),
  ]);
}

export default slice.reducer;
